import { createSlice } from "@reduxjs/toolkit";

/** Must match `DELETED_ITEM_TYPE_ORDER` in `pages/app/deleted-items/index.jsx` */
export const DELETED_ITEM_TYPES_FOR_STORE = [
  "Company",
  "Contact",
  "Opportunity",
  "Document",
  "Product/Service",
  "Dashboard",
  "Task",
  "Template",
  "Workflow",
  "Sequence",
  "Document Wizard Definition"
];

const defaultPagination = () => ({
  skip: 0,
  limit: 10,
  sortInfo: [],
  groupBy: [],
  filterValue: []
});

const emptyTypeBucket = () => ({
  isFetched: false,
  isLoading: false,
  deletedItems: [],
  count: 0,
  pagination: defaultPagination()
});

const initialDeletedItemsByType = DELETED_ITEM_TYPES_FOR_STORE.reduce((acc, typeName) => {
  acc[typeName] = emptyTypeBucket();
  return acc;
}, {});

const initialDeletedItemCountsByModule = DELETED_ITEM_TYPES_FOR_STORE.reduce((acc, typeName) => {
  acc[typeName] = 0;
  return acc;
}, {});

export const deletedItemsSlice = createSlice({
  name: "deleted-items",
  initialState: {
    columnNames: [],
    deletedItemsByType: initialDeletedItemsByType,
    isLoading: false,
    deletedItemCountsByModule: initialDeletedItemCountsByModule,
    deletedItemCountsByModuleLoading: false,
    deletedItemCountsByModuleFetched: false
  },
  reducers: {
    onCallDeletedItemCountsByModule: (state) => {
      state.deletedItemCountsByModuleLoading = true;
    },
    onSuccessDeletedItemCountsByModule: (state, { payload }) => {
      state.deletedItemCountsByModuleLoading = false;
      state.deletedItemCountsByModuleFetched = true;
      state.deletedItemCountsByModule = payload;
    },
    onFailedDeletedItemCountsByModule: (state) => {
      state.deletedItemCountsByModuleLoading = false;
      state.deletedItemCountsByModuleFetched = true;
      DELETED_ITEM_TYPES_FOR_STORE.forEach((typeName) => {
        state.deletedItemCountsByModule[typeName] = 0;
      });
    },
    onCallDeletedItemsForType: (state, { payload: { typeName, paginationData } }) => {
      const bucket = state.deletedItemsByType[typeName];
      if (!bucket) {
        return;
      }
      bucket.isLoading = true;
      bucket.pagination = paginationData;
      bucket.deletedItems = [];
    },
    onSuccessDeletedItemsForType: (state, { payload: { typeName, data } }) => {
      const bucket = state.deletedItemsByType[typeName];
      if (!bucket) {
        return;
      }
      bucket.isFetched = true;
      bucket.isLoading = false;
      bucket.count = parseInt(data?.total_count, 10) || 0;
      bucket.deletedItems = data.tableData || [];
      state.deletedItemCountsByModule[typeName] = bucket.count;
      if (data.columnNames?.length && !state.columnNames.length) {
        state.columnNames = data.columnNames;
      }
    },
    onFailedDeletedItemsForType: (state, { payload: { typeName } }) => {
      const bucket = state.deletedItemsByType[typeName];
      if (!bucket) {
        return;
      }
      bucket.isFetched = true;
      bucket.isLoading = false;
      bucket.deletedItems = [];
      bucket.count = 0;
    },
    switchDeletedItemsLoading: (state, { payload }) => {
      state.isLoading = payload !== undefined ? payload : !state.isLoading;
    },
    onSuccessRestoreItems: (state, { payload }) => {
      const idSet = new Set(payload);
      DELETED_ITEM_TYPES_FOR_STORE.forEach((typeName) => {
        const bucket = state.deletedItemsByType[typeName];
        const removed = bucket.deletedItems.filter((item) => idSet.has(item.id)).length;
        bucket.deletedItems = bucket.deletedItems.filter((item) => !idSet.has(item.id));
        bucket.count = Math.max(0, bucket.count - removed);
        if (removed) {
          state.deletedItemCountsByModule[typeName] = Math.max(
            0,
            (state.deletedItemCountsByModule[typeName] || 0) - removed
          );
        }
      });
    }
  }
});

export const {
  onCallDeletedItemsForType,
  onSuccessDeletedItemsForType,
  onFailedDeletedItemsForType,
  switchDeletedItemsLoading,
  onSuccessRestoreItems,
  onCallDeletedItemCountsByModule,
  onSuccessDeletedItemCountsByModule,
  onFailedDeletedItemCountsByModule
} = deletedItemsSlice.actions;

export default deletedItemsSlice.reducer;
